import type { AdminReleaseRegression } from "../../contracts/admin-read-models";
import type { AdminDashboardLocale } from "./admin-dashboard-locale";
import { pickAdminDashboardLocaleText } from "./admin-dashboard-copy";

type AdminReleaseRow = AdminReleaseRegression["items"][number];

export type AdminReleaseRowSnapshotItemViewModel = {
  id: "peakErrorRate" | "releaseJobDelta" | "dominantPlatform";
  label: string;
  value: string;
};

export type AdminReleaseRowSnapshotViewModel = {
  title: string;
  sourceGapLabel: string;
  items: AdminReleaseRowSnapshotItemViewModel[];
};

function text(locale: AdminDashboardLocale, en: string, ko: string): string {
  return locale === "ko" ? ko : en;
}

function formatErrorRate(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}

function findPeakErrorRow(rows: AdminReleaseRow[]): AdminReleaseRow | undefined {
  return [...rows].sort((left, right) => {
    if (right.errorRate !== left.errorRate) {
      return right.errorRate - left.errorRate;
    }

    return right.requestCount - left.requestCount;
  })[0];
}

function buildPeakErrorRateItem(
  rows: AdminReleaseRow[],
  locale: AdminDashboardLocale,
): AdminReleaseRowSnapshotItemViewModel | undefined {
  const topRow = findPeakErrorRow(rows);
  if (!topRow) {
    return undefined;
  }

  return {
    id: "peakErrorRate",
    label: text(locale, "Peak error rate", "최고 오류율"),
    value: [formatErrorRate(topRow.errorRate), topRow.topErrorCode].filter(Boolean).join(" / "),
  };
}

function buildReleaseJobDeltaItem(
  rows: AdminReleaseRow[],
  locale: AdminDashboardLocale,
): AdminReleaseRowSnapshotItemViewModel | undefined {
  const maxDelta = rows.reduce<number | undefined>((currentMax, row) => {
    if (row.jobFailureDelta === undefined) {
      return currentMax;
    }

    if (currentMax === undefined || row.jobFailureDelta > currentMax) {
      return row.jobFailureDelta;
    }

    return currentMax;
  }, undefined);

  if (maxDelta === undefined) {
    return undefined;
  }

  return {
    id: "releaseJobDelta",
    label: text(locale, "Release job delta", "릴리스 작업 증감"),
    value: maxDelta > 0 ? text(locale, "Elevated", "상승") : text(locale, "Stable", "안정"),
  };
}

function buildDominantPlatformItem(
  rows: AdminReleaseRow[],
  locale: AdminDashboardLocale,
): AdminReleaseRowSnapshotItemViewModel | undefined {
  const topRow = findPeakErrorRow(rows);
  if (!topRow?.platformConcentration) {
    return undefined;
  }

  const topPlatform = Object.entries(topRow.platformConcentration)
    .sort((left, right) => right[1] - left[1])[0]?.[0];

  if (!topPlatform) {
    return undefined;
  }

  return {
    id: "dominantPlatform",
    label: text(locale, "Dominant platform", "지배 플랫폼"),
    value: text(locale, `${topPlatform.toUpperCase()} dominant`, `${topPlatform.toUpperCase()} 집중`),
  };
}

export function buildAdminReleaseRowSnapshotViewModel(
  rows: AdminReleaseRow[],
  locale: AdminDashboardLocale,
): AdminReleaseRowSnapshotViewModel | undefined {
  const items = [
    buildPeakErrorRateItem(rows, locale),
    buildReleaseJobDeltaItem(rows, locale),
    buildDominantPlatformItem(rows, locale),
  ].flatMap((item) => (item ? [item] : []));

  if (items.length === 0) {
    return undefined;
  }

  return {
    title: pickAdminDashboardLocaleText(locale, {
      en: "Current row snapshot",
      ko: "현재 행 스냅샷",
    }),
    sourceGapLabel: pickAdminDashboardLocaleText(locale, {
      en: "Derived from the visible release rows only. Use it to choose the next filter or drilldown, not as proof that upstream observability aggregates are populated.",
      ko: "이 화면에 이미 로드된 visible release rows만으로 다시 계산한 snapshot입니다. 다음 필터나 drilldown을 고르는 데 사용하고, upstream 관측 집계 source가 채워졌다는 증거로 읽지 마세요.",
    }),
    items,
  };
}
